/**
 * Hotel words for toasts and the bell (twin slice 1, plan W5 step 12).
 *
 * FOH already get toasts today, and they carry the engine's text verbatim:
 * "phantas_01 is reporting an issue: DOORWAY_BLOCKED: SL1.", "SL1 is
 * refusing robot rides (LIFT_MODE_NOT_NORMAL, repeated)". For a SITE
 * audience the rules below render through the same copy tables the
 * attention list uses -- no codes, no "RMF", no task ids, and the time on
 * the site's clock with its day named. Everything else, and everything for
 * ops, stays the engine's own text.
 *
 * NOT behind the attention flags: the toasts exist today, so their words
 * should be the hotel's today. W4's Toast and AlertBell call this.
 *
 * `lift_lanes_closed` and `lift_no_reading` are W7's rules (off until D9);
 * they are worded here so that the first time one fires, FOH read words,
 * not a code (plan X34). Task attention, the edge box's services and the
 * door rules joined the list with amendment A12; `lift_gate_disarmed`
 * (lift control off, A14, off until D9) for the same reason as W7's.
 * `fire_mode`, `task_failed` and the two automatic-charging rules joined
 * with copy deck v2 (break N2): with them every rule the server raises
 * reads in hotel words for a site audience. The fire toast says exactly
 * what the attention column's fire row says (`FIRE_ALARM`,
 * `FIRE_HOLD_WORDS`), for a drill too, never a site id or "Dispatch"; a
 * failed job is never subject-lined by its task
 * id; automatic charging never names "the fleet adapter".
 * `door_open_failed`, `door_passage_stalled` and `robot_stalled`
 * (api-server 0.6.42) are not on the list, on purpose: the engine writes
 * them in the hotel's words already, naming the robot waiting at a door,
 * which these tables cannot (a toast does not read the door's live state),
 * so a site reader gets the engine's text (`alert-rules.json`,
 * `engine_text`). Their attention cards are worded from the copy tables.
 *
 * Raw place, door and lift ids are left as the engine names them where no
 * plain name exists (Decision 13: no label file); the copy pass decides.
 *
 * COPY STATUS: pending Akash's copy pass 2 (plan gate B).
 */
import type { Audience } from '../../auth/roles';
import { alertRule, alertSubject, type AttentionAlert } from './alert-subject';
import { SERVER_RULE_DWELLS } from './evidence';
import { liftCodeClass, liftRefusalWords } from './lift-codes';
import { NEEDS_A_CHECK, robotCopyForCategory, robotCopyRow, type RobotCopyRow } from './robot-copy';
import { FIRE_ALARM, FIRE_HOLD_WORDS, dayClockText, type SiteTimeZone } from './words';

/** The door rules (`alerts_engine.py`): two about one door, three about
 *  the adapter process that drives several. */
const DOOR_RULES = [
  'door_unknown',
  'door_controller_offline',
  'door_gate_disarmed',
  'door_vendor_link_down',
  'door_adapter_silent',
] as const;

/** The rules a site audience reads in hotel words. */
export const HOTEL_WORD_RULES: ReadonlySet<string> = new Set([
  'robot_issue',
  'robot_unreachable',
  'robot_error',
  'lift_refusal',
  'lift_fault',
  'lift_lanes_closed',
  'lift_no_reading',
  // Amendment A14.
  'lift_gate_disarmed',
  // Amendment A12.
  'task_attention',
  'task_long_running',
  'edge_gateway',
  // A silent site box, and only its health reports: Nexsoss's to act on.
  'edge_box_offline',
  'edge_agent_silent',
  ...DOOR_RULES,
  // Copy deck v2, N2.
  'fire_mode',
  'task_failed',
  'idle_behavior_diverged',
  'idle_behavior_expired',
]);

/**
 * Rules whose row is a POINT event -- something that happened once, not a
 * condition that holds -- so its time is when it was reported, never a
 * "Since": a failed job has not been failing since 14:31.
 */
const POINT_EVENT_RULES: ReadonlySet<string> = new Set(['task_failed', 'idle_behavior_expired']);

export interface ToastWords {
  /** The toast's subject line: the robot, the lift, or what else it is
   *  about, in the reader's words. */
  source: string;
  message: string;
  /** False when this is the engine's own text, unchanged. */
  translated: boolean;
}

/** An ops-only report, or a category the copy table does not know,
 *  reaches a hotel toast as this, never as its code. */
export const OPS_ONLY_TOAST = NEEDS_A_CHECK;
/** A hotel-word rule whose row could not be read. */
export const UNREADABLE_TOAST = 'Needs a check';

/** Subject lines for rows about no one robot, lift or door (A12). */
export const TOAST_SOURCES = {
  siteBox: 'Site box',
  doors: 'Doors',
  /** A stuck job the engine names no robot for. */
  job: 'Robot job',
  /** The site's lift control (A14): every lift, never a site id. */
  lifts: 'Lifts',
  /** The fire alarm, real or a drill (N2; `words.ts`): never the site id. */
  fire: FIRE_ALARM,
  /** A robot row whose robot the row does not name. */
  robot: 'Robot',
} as const;

/** A failed job (`task_failed`, N2): the engine names only the task. */
export const JOB_FAILED_WORDS = "A robot's job didn't finish";
/** `idle_behavior_diverged` (N2): switched off, still on. */
export const AUTO_CHARGE_STILL_ON_WORDS =
  'Automatic charging was switched off but is still on, so it may go to its charger when idle';
/** `idle_behavior_expired` (N2): the off timer ran out. */
export const AUTO_CHARGE_BACK_ON_WORDS =
  'Automatic charging is back on because its off timer ran out; it will go to its charger when idle';

/** A robot rule's robot, bare: the automatic-charging rules once stamped
 *  `fleet/robot` (`alerts_engine.py`'s module note). */
function bareRobot(id: string): string {
  const slash = id.lastIndexOf('/');
  return slash >= 0 ? id.slice(slash + 1) : id;
}

/** `lift_gate_disarmed`'s words: the console's own site row for the same
 *  moment (W6's "Lift control off" row), so a toast and the list never
 *  say it two ways. */
export const LIFT_CONTROL_OFF_WORDS = "Robots can't use any lift: lift control is off";

function robotHeadline(row: RobotCopyRow, robot: string, arg: string | null): string {
  // An unknown category's raw text is never a site reader's (the card
  // hides it too, `select.ts`); a toast has its own "no codes" rule (plan
  // W5 step 12), and the adapter's categories include code-style prefixes
  // (DOORWAY_BLOCKED: ...), so the next new one would be a code.
  return row.opsOnly || row.key === 'unmatched'
    ? OPS_ONLY_TOAST
    : row.headline({ robot, arg, level: null });
}

/**
 * `lift_fault`, in words the engine's message can back
 * (`alerts_engine._lift_fault_message`, every variant pinned by
 * `tests/fixtures/contracts/alert-messages.json`).
 *
 * NEVER "a fault". The worker's `fault` flag is MODE_OFFLINE or
 * MODE_EMERGENCY, and MODE_OFFLINE is also what a disarmed site publishes
 * for every car: an older server, or one that could not read the site's
 * write gate, raises this row for a disarm. So only the two messages that
 * rule the gate out are said as the car's own state; any other row says
 * only what holds for every one of them, a disarm included.
 *
 * Nor "out of service" for a car Nexsoss has only stopped hearing (the
 * first line of 2026-11-02): nothing reported that, and the car may well be
 * carrying guests. It says who can't see what, and that this is not news
 * about the car. Nexsoss's to act on: the box or the link, never the hotel.
 */
function liftFaultWords(lift: string, message: string): string {
  if (message.startsWith(`${lift} reports an emergency mode`)) {
    return `${lift} reports an emergency mode`;
  }
  if (message.startsWith(`Nexsoss can't see ${lift}`)) {
    return `Nexsoss can't see ${lift} right now, which doesn't mean it's out of service`;
  }
  if (message.startsWith(`${lift} reports itself offline while lift control is on`)) {
    return `${lift} is out of service, or its link is down`;
  }
  return `Robots can't use ${lift} right now`;
}

/** "the robots", "the lifts", "the doors" for an edge service named for
 *  its bridge (`robot`, `lift`, `door` in `edge/bootstrap.sh`; older
 *  boxes said `robot_bridge`); null for one this table does not know. */
function edgeServicePart(service: string): string | null {
  if (/robot/i.test(service)) return 'the robots';
  if (/lift/i.test(service)) return 'the lifts';
  if (/door/i.test(service)) return 'the doors';
  return null;
}

/** `"Edge <service> service is <state>"` (`report_edge_gateway`). */
function edgeGatewayWords(service: string, message: string): string {
  const m = new RegExp(`^Edge ${service.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} service is (\\w+)`).exec(
    message,
  );
  const state = m ? m[1] : null;
  const how =
    state === 'down'
      ? 'is down'
      : state === 'unreachable'
        ? "isn't answering"
        : state === 'degraded'
          ? "isn't working properly"
          : "isn't working normally";
  const part = edgeServicePart(service);
  return part ? `The site box's link to ${part} ${how}` : `Part of the site box ${how}`;
}

/**
 * `edge_box_offline`: what stopped, what that takes out of sight, and whose
 * it is. Never that the lifts are fine (`edge_box.offline_message`). The list
 * is the engine's own last sentence, in words a reader knows already.
 */
function boxOfflineWords(message: string): string {
  const m = /Can't see (.+)\.$/.exec(message);
  const parts = m ? `the ${m[1]!.replace(/, /g, ', the ').replace(/ and /g, ' and the ')}` : 'this site';
  return `The site box has stopped reporting, so Nexsoss can't see ${parts}. Nexsoss deals with this`;
}

/** The door rules, for a site reader. The door's own id stays on the
 *  subject line only: the registry has no plainer name for it. */
function doorWords(rule: (typeof DOOR_RULES)[number]): string {
  switch (rule) {
    case 'door_unknown':
      return "Can't tell whether this door is open or shut";
    case 'door_controller_offline':
      return "This door's controller is offline, so robots won't go through it";
    case 'door_gate_disarmed':
      return "Robots can't open doors: door control is switched off";
    case 'door_vendor_link_down':
      return "Robots can't open doors: Nexsoss can't reach the door system";
    case 'door_adapter_silent':
      return "Robots can't open doors: Nexsoss has stopped hearing from door control";
  }
}

const isDoorRule = (rule: string): rule is (typeof DOOR_RULES)[number] =>
  (DOOR_RULES as readonly string[]).includes(rule);

/**
 * The rules whose row opens only after its condition has held for the
 * rule's whole grace, measured by the engine from when it first saw it:
 * `lift_lanes_closed` (`_LIFT_LANES_CLOSED_GRACE_S`) and
 * `lift_gate_disarmed` (`_LIFT_GATE_DISARMED_GRACE_S`, A14), and
 * `edge_box_offline` (three minutes of silence).
 */
const OPENS_AFTER_ITS_GRACE: ReadonlySet<string> = new Set([
  'lift_lanes_closed',
  'lift_gate_disarmed',
  'edge_box_offline',
]);

/**
 * The moment a hotel toast's "Since" names, or null when it names none.
 *
 * The row's opening, except for a rule that opens only after its grace
 * (`OPENS_AFTER_ITS_GRACE`, `SERVER_RULE_DWELLS`): its opening is two
 * minutes AFTER the closure, or the disarm, it reports -- and that may be
 * older still (the engine saw it late, or restarted). "Since at least"
 * the opening less the grace is what the row can back.
 */
export function toastSince(alert: AttentionAlert): { ms: number; atLeast: boolean } | null {
  const opened = alert.openedAtMs;
  if (typeof opened !== 'number' || !Number.isFinite(opened)) return null;
  const rule = alertRule(alert);
  if (rule !== null && OPENS_AFTER_ITS_GRACE.has(rule)) {
    return { ms: opened - SERVER_RULE_DWELLS[rule]!.dwellMs, atLeast: true };
  }
  return { ms: opened, atLeast: false };
}

/**
 * `wallNowMs` is this device's wall clock, read only to name the day of
 * the "Since" ("yesterday 23:58", "27 Sep 23:58"): without it a toast
 * left from last night read as this morning's.
 */
export function toastWordsFor(
  alert: AttentionAlert,
  audience: Audience,
  clock: SiteTimeZone,
  wallNowMs: number,
): ToastWords {
  const id = alert.sourceId || alert.source || '';
  const raw: ToastWords = { source: id, message: alert.message, translated: false };
  const rule = alertRule(alert);
  if (audience === 'ops' || !rule || !HOTEL_WORD_RULES.has(rule)) return raw;

  let source = id;
  let words: string;
  const subject = alertSubject(alert);
  if (isDoorRule(rule)) {
    // A door's own id where it is about one door (no plainer name exists);
    // the adapter rules are about an adapter process, never named.
    if (alert.sourceKind !== 'door') source = TOAST_SOURCES.doors;
    words = doorWords(rule);
  } else if (rule === 'fire_mode') {
    // The column's own words, drill or not ("FIRE DRILL active at <site>"
    // and "FIRE MODE active at <site>" alike): the column and the shell's
    // overlay say "Fire alarm" for both (`words.ts`). The row's source is
    // the site's id.
    source = TOAST_SOURCES.fire;
    words = FIRE_HOLD_WORDS;
  } else if (rule === 'task_failed') {
    // The task id is the row's source: never a hotel word.
    source = TOAST_SOURCES.job;
    words = JOB_FAILED_WORDS;
  } else if (rule === 'idle_behavior_diverged' || rule === 'idle_behavior_expired') {
    source = bareRobot(id) || TOAST_SOURCES.robot;
    words =
      rule === 'idle_behavior_diverged' ? AUTO_CHARGE_STILL_ON_WORDS : AUTO_CHARGE_BACK_ON_WORDS;
  } else {
    switch (subject?.kind) {
      case 'robot_issue': {
        const { row, arg } = robotCopyForCategory(subject.category);
        words = robotHeadline(row, subject.robot, arg);
        break;
      }
      case 'robot_error':
        words = robotHeadline(robotCopyRow('robot_error'), subject.robot, null);
        break;
      case 'robot_unreachable':
        words = robotHeadline(robotCopyRow('state_channel_stale'), subject.robot, null);
        break;
      case 'task_attention': {
        // The task id is the row's source, never a hotel word: the robot
        // is the subject, and the job is "its job".
        const stuck = robotCopyRow(subject.liftRule ? 'stuck_task_lift_rule' : 'stuck_task');
        source = subject.robot ?? TOAST_SOURCES.job;
        words = subject.robot
          ? robotHeadline(stuck, subject.robot, null)
          : subject.liftRule
            ? "A robot's job may be waiting for a blocked lift"
            : "A robot's job isn't moving forward";
        break;
      }
      case 'task_long_running': {
        const long = robotCopyRow('long_task');
        const minutes = subject.minutes === null ? null : String(subject.minutes);
        source = subject.robot ?? TOAST_SOURCES.job;
        words = subject.robot
          ? robotHeadline(long, subject.robot, minutes)
          : `A robot's job has been running for over ${minutes ?? '30'} minutes`;
        break;
      }
      case 'lift_refusal':
        words = liftRefusalWords(subject.lift, liftCodeClass(subject.code));
        break;
      case 'lift_fault':
        words = liftFaultWords(subject.lift, alert.message);
        break;
      case 'lift_lanes_closed':
        // The closure, as the card says it ("Kept out since ..."), never
        // the car as its subject: the rule fires while the car says it
        // CAN take robots, and it is the adapter that closed the lanes.
        words = `Robots kept out of ${subject.lift}, though the car says it can take them`;
        break;
      case 'lift_no_reading':
        words = `No fresh reading from ${subject.lift}; whether robots can use it is not known`;
        break;
      case 'lift_gate_disarmed':
        // The row's source is the site's id, never a hotel word.
        source = TOAST_SOURCES.lifts;
        words = LIFT_CONTROL_OFF_WORDS;
        break;
      case 'edge_gateway':
        source = TOAST_SOURCES.siteBox;
        words = edgeGatewayWords(subject.service, alert.message);
        break;
      case 'edge_box_offline':
        source = TOAST_SOURCES.siteBox;
        words = boxOfflineWords(alert.message);
        break;
      case 'edge_agent_silent':
        source = TOAST_SOURCES.siteBox;
        words = "The site box is up but has stopped sending Nexsoss its health reports. Nexsoss deals with this";
        break;
      default:
        // A hotel-word rule whose row cannot be read: say so plainly rather
        // than fall back to engine text that may carry a code. The source
        // line too: for a task row it is a task id.
        if (rule === 'task_attention') source = TOAST_SOURCES.job;
        if (rule === 'edge_gateway' || rule === 'edge_box_offline' || rule === 'edge_agent_silent') {
          source = TOAST_SOURCES.siteBox;
        }
        if (rule === 'lift_gate_disarmed') source = TOAST_SOURCES.lifts;
        words = UNREADABLE_TOAST;
    }
  }
  const at = toastSince(alert);
  const when = at ? dayClockText(at.ms, clock, wallNowMs) : null;
  const since =
    when === null
      ? ''
      : POINT_EVENT_RULES.has(rule)
        ? ` Reported ${when}.`
        : ` Since ${at!.atLeast ? 'at least ' : ''}${when}.`;
  return { source, message: `${words}.${since}`, translated: true };
}
